import { useEffect, useState } from 'react'
import { useVideoStore, type VideoSortBy } from '../../stores'
import { videoService } from '../../services'
import { Input } from '../common'

interface VideoFiltersProps {
  showVisibility?: boolean
}

export function VideoFilters({ showVisibility = false }: VideoFiltersProps) {
  const { filters, setFilter, resetFilters } = useVideoStore()
  const [categories, setCategories] = useState<string[]>([])
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const [searchInput, setSearchInput] = useState(filters.search)

  // Fetch categories and tags on mount
  useEffect(() => {
    videoService.getCategories().then(setCategories).catch(console.error)
    videoService.getTags().then(setAvailableTags).catch(console.error)
  }, [])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilter('search', searchInput)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchInput, filters.search, setFilter])

  const sortOptions: { value: VideoSortBy; label: string }[] = [
    { value: 'newest', label: 'Newest' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'title', label: 'A-Z' },
  ]

  const handleTagToggle = (tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag]
    setFilter('tags', newTags)
  }

  const hasActiveFilters =
    filters.search ||
    filters.category ||
    filters.tags.length > 0 ||
    filters.sortBy !== 'newest' ||
    (showVisibility && filters.visibility !== 'all')

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
      {/* Search */}
      <div>
        <Input
          placeholder="Search videos..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          leftIcon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          }
        />
      </div>

      {/* Category & Sort Row */}
      <div className="flex flex-wrap gap-4">
        {/* Category filter */}
        <div className="flex-1 min-w-[150px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={filters.category || ''}
            onChange={(e) => setFilter('category', e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Sort */}
        <div className="flex-1 min-w-[150px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sort By
          </label>
          <select
            value={filters.sortBy}
            onChange={(e) => setFilter('sortBy', e.target.value as VideoSortBy)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Visibility (for dashboard) */}
        {showVisibility && (
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Visibility
            </label>
            <select
              value={filters.visibility}
              onChange={(e) =>
                setFilter('visibility', e.target.value as typeof filters.visibility)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="organization">Organization</option>
            </select>
          </div>
        )}
      </div>

      {/* Tags */}
      {availableTags.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags
          </label>
          <div className="flex flex-wrap gap-2">
            {availableTags.slice(0, 12).map((tag) => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  filters.tags.includes(tag)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Clear filters */}
      {hasActiveFilters && (
        <div className="flex justify-end pt-2 border-t border-gray-100">
          <button
            onClick={() => {
              resetFilters()
              setSearchInput('')
            }}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  )
}
